/**
 * The pick-up of "Price curve of a date" (CU-869fmjzrg D-F, CA 4-5): a date or a week, relative ("Next Saturday",
 * "In N days", "Next week"…) or fixed. A relative one is a key resolved against today every time, so a copied link or
 * the remembered view opened another day moves with it. A week is sent as any day of it: the server resolves its
 * Sunday-to-Saturday week of the data (no second week decoder here).
 *
 * Pure and alias-free: `node --test` runs it.
 */
import { addDays, daysBetween, fmtRange, fmtWeekday, fromIso, startOfDay, toIso } from '../../../@services/dates.js'

export const PICKS = Object.freeze(['date', 'week'])
export const DATE_WHEN = Object.freeze({ nextSat: 'Next Saturday', sat2: 'Saturday after next', sat3: 'Saturday in 3 weeks', inN: 'In N days', fixed: 'Fixed date' })
export const WEEK_WHEN = Object.freeze({ nextWeek: 'Next week', in2w: 'In 2 weeks', in3w: 'In 3 weeks', fixed: 'Fixed week' })
export const DEFAULT_WHEN = Object.freeze({ date: 'nextSat', week: 'nextWeek' })
export const MAX_IN_DAYS = 365

/** The first Saturday strictly after today (Curro's decision 10: the default pick-up) */
export function nextSaturday(today) {
	const t = startOfDay(today)
	const ahead = (6 - t.getDay() + 7) % 7 || 7
	return addDays(t, ahead)
}

export const isWhen = (pick, when) => Object.prototype.hasOwnProperty.call(pick === 'week' ? WEEK_WHEN : DATE_WHEN, when)

/** The day sent as `pickup` (a `Date`), or `null` when the fixed date or N is missing */
export function resolvePickup({ pick = 'date', when, n = null, date = null }, today) {
	const t = startOfDay(today)
	if (pick === 'week') {
		const k = { nextWeek: 1, in2w: 2, in3w: 3 }[when]
		if (k) return addDays(t, 7 * k)
		return when === 'fixed' ? fromIso(date) : null
	}
	if (when === 'nextSat') return nextSaturday(t)
	if (when === 'sat2') return addDays(nextSaturday(t), 7)
	if (when === 'sat3') return addDays(nextSaturday(t), 14)
	if (when === 'inN') return Number.isInteger(n) && n >= 0 && n <= MAX_IN_DAYS ? addDays(t, n) : null
	if (when === 'fixed') return fromIso(date)
	return null
}

/** `Next Saturday` · `In 12 days` · `Fixed date` … (the field's value) */
export function whenLabel({ pick = 'date', when, n }) {
	if (pick === 'date' && when === 'inN') return Number.isInteger(n) ? `In ${n} day${n === 1 ? '' : 's'}` : DATE_WHEN.inN
	return (pick === 'week' ? WEEK_WHEN : DATE_WHEN)[when] || ''
}

/** The line under the field: `Sat 31 Oct 2026 · in 5 days`, `1 Nov – 7 Nov 2026 · past`, `Fixed week · in 9 days` */
export function pickupNote({ pick = 'date', when }, pickupDates, today) {
	const dates = (pickupDates || []).map(fromIso).filter(Boolean)
	if (!dates.length) return ''
	const first = dates[0], last = dates[dates.length - 1]
	const d = daysBetween(startOfDay(today), first)
	const dist = last < startOfDay(today) ? 'past' : d === 0 ? 'today' : d > 0 ? `in ${d} day${d === 1 ? '' : 's'}` : 'this week'
	const what = pick === 'week' ? fmtRange(first, last) : fmtWeekday(first)
	return `${when === 'fixed' ? (pick === 'week' ? 'Fixed week: ' : '') : ''}${what} · ${dist}`
}

export const pickupIso = (state, today) => {
	const d = resolvePickup(state, today)
	return d ? toIso(d) : null
}
